import React, { useRef, useState, useEffect } from 'react';
import Head from 'next/head';
import { useNotificationContext } from '../../components/shared/notification';
import { colors, GridContainer, GridItem } from '../shared/ui-extension';
import { ItemSearchInput } from './item-search-input';
import { Box, Link, Stack, Typography, Button } from '@mui/material';
import { ItemWarehouseDetails } from './item-warehouse-details';
import { ItemNotes } from './item-notes';
import ItemDetails from './item-details';
import { Item as ItemType } from './item-types';
import ItemCustomerUsage from './item-customer-usage';
import ActiveQuotesHost from './active-quotes-host';
import ItemDocumentLinks from '../shared/item/item-document-links';
import ItemShareButton from './item-share-button';
import ItemEditButton from './item-edit-button';
import PropertyCard from './property-card';
import ItemHistoryTable from './item-history-table';
import {
  ItemInternalPriceInfo,
  ItemInternalSupplierInfo,
} from './internal-information';

export const Item = () => {
  const { updateQueryParameters, queryParameters } = useNotificationContext();

  const [selectedItem, _setSelectedItem] = useState<ItemType | undefined>();
  const itemHistoryRef = useRef(null);
  const scrollToHistoryRef = () => {
    itemHistoryRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const setSelectedItem = (item: ItemType) => {
    _setSelectedItem(item);
    updateQueryParameters({ itemId: item.item_id });
  };
  const itemId = ((queryParameters.itemId as string) || '').toUpperCase();
  const itemSearchInput = () => (
    <ItemSearchInput
      defaultItemId={itemId}
      onItemSelected={(selectedItem) => {
        setSelectedItem(selectedItem);
      }}
    />
  );

  // Query strings are always passed in as lower case from P21 ribbon metrics
  useEffect(() => {
    if (queryParameters.itemid) {
      updateQueryParameters({ itemId: queryParameters.itemid as string });
    }
  }, [queryParameters.itemid]);

  const itemLinkToWebsite = `https://www.containerandpackaging.com/item/${itemId}`;

  return (
    <GridContainer alignItems='stretch' spacing={3} rowSpacing={3}>
      <Head>
        <title>
          {(selectedItem?.item_id &&
            `${selectedItem?.item_id}: ${selectedItem?.extended_desc}`) ||
            'Item Details'}
        </title>
        <meta name='viewport' content='initial-scale=1.0, width=device-width' />
      </Head>
      <GridItem xs={12} lg={5}>
        <Stack direction={'row'} justifyContent={'start'}>
          <Box width={'500px'}>{itemSearchInput()}</Box>
        </Stack>
      </GridItem>
      <GridItem xs={12} lg={1}>
        <Typography
          color={colors.orange}
          fontStyle={'italic'}
          fontWeight={'bold'}
          variant='subtitle1'
        >
          {selectedItem?.pam_status}
        </Typography>
      </GridItem>
      <GridItem xs={12} lg={6}>
        {selectedItem && (
          <Stack
            gap={1}
            useFlexGap
            direction={'row'}
            justifyContent={{ lg: 'end' }}
          >
            <Button variant='contained' onClick={() => scrollToHistoryRef()}>
              Item History
            </Button>
            <ItemShareButton
              item={selectedItem}
              onClick={() =>
                window.open(
                  `/item/share-item/${selectedItem.item_id}`,
                  '_blank'
                )
              }
            />
            <ItemEditButton item={selectedItem} />
            <ItemDocumentLinks itemId={selectedItem.item_id} />
            <ActiveQuotesHost item={selectedItem} />
          </Stack>
        )}
      </GridItem>
      {selectedItem && (
        <GridItem xs={12}>
          <GridContainer alignItems={'flex-start'}>
            <GridItem xs={12} lg={7}>
              <Stack>
                <Typography variant='h5' style={{ fontWeight: 'bold' }}>
                  <Link href={itemLinkToWebsite} target='_blank'>
                    {selectedItem.item_id}
                  </Link>
                </Typography>
                <Typography variant='body2' style={{ fontSize: '18px' }}>
                  {selectedItem.extended_desc}
                </Typography>
              </Stack>
            </GridItem>
            <GridItem xs={12} lg={5}>
              <GridContainer alignItems={'flex-start'}>
                <GridItem xs={12} lg={6}>
                  <PropertyCard>
                    <ItemInternalSupplierInfo item={selectedItem} />
                  </PropertyCard>
                </GridItem>
                <GridItem xs={12} lg={6}>
                  <PropertyCard>
                    <ItemInternalPriceInfo item={selectedItem} />
                  </PropertyCard>
                </GridItem>
              </GridContainer>
            </GridItem>
          </GridContainer>
        </GridItem>
      )}
      <GridItem xs={12}>
        {selectedItem?.inv_mast_uid && <ItemDetails item={selectedItem} />}
      </GridItem>
      <GridItem xs={12}>
        {selectedItem?.item_id && (
          <PropertyCard
            title={`Warehouse Details for Item ${selectedItem.item_id}`}
          >
            <ItemWarehouseDetails
              itemId={selectedItem?.item_id}
              vendorId={selectedItem?.vendor_id}
            />
          </PropertyCard>
        )}
      </GridItem>
      <GridItem xs={12}>
        {selectedItem?.item_id && (
          <PropertyCard title='Item Notes'>
            <ItemNotes item={selectedItem} />
          </PropertyCard>
        )}
      </GridItem>

      {selectedItem && (
        <GridItem xs={12}>
          <PropertyCard
            title={`Customer Usage for Item ${selectedItem.item_id}`}
          >
            <ItemCustomerUsage itemID={selectedItem.item_id} />
          </PropertyCard>
        </GridItem>
      )}

      {selectedItem && (
        <GridItem xs={12}>
          <div ref={itemHistoryRef}>
            <PropertyCard title='Item History'>
              <ItemHistoryTable item={selectedItem} />
            </PropertyCard>
          </div>
        </GridItem>
      )}
    </GridContainer>
  );
};
